/**
 * Closing Disclosure View - Container/Wrapper
 *
 * Fetches order data and passes it to the main ClosingDisclosureView component.
 * Handles loading and error states.
 */

import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { ClosingDisclosureView } from './closing-disclosure-view';
import { getOrder } from 'services/querys/orders';
import BackdropLoading from 'components/common/BackdropLoading';
import { AccordionProvider } from 'contexts/accordion-context';

interface RouteParams {
  id?: string;
}

const ClosingDisclosureViewContainer = () => {
  const { id: orderId } = useParams<RouteParams>();
  const [error, setError] = useState<string | null>(null);

  const {
    data: order,
    isLoading,
    refetch,
  } = useQuery(
    ['order', orderId],
    () => {
      if (!orderId) {
        throw new Error('Order ID not found');
      }
      return getOrder(orderId);
    },
    {
      enabled: !!orderId,
      onError: (err: any) => {
        const errorMessage = err?.message || 'Failed to load order data';
        setError(errorMessage);
      },
    }
  );

  const handleRetry = useCallback(() => {
    setError(null);
    refetch();
  }, [refetch]);

  if (!orderId) {
    return (
      <AccordionProvider>
        <ClosingDisclosureView
          order={{} as any}
          isLoading={false}
          error="Order ID not found"
        />
      </AccordionProvider>
    );
  }

  if (isLoading) {
    return <BackdropLoading open loadingText="Loading closing disclosure..." />;
  }

  return (
    <AccordionProvider>
      <ClosingDisclosureView
        order={(order as any)?.getOrder}
        isLoading={isLoading}
        error={error}
        onRetry={handleRetry}
      />
    </AccordionProvider>
  );
};

export default ClosingDisclosureViewContainer;
